import React from 'react';
import SEOHead from '../components/SEOHead';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  Book, Code2, Zap, Shield, Database, Headphones,
  ArrowRight, ExternalLink, Search, ChevronRight
} from 'lucide-react';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

// PLACEHOLDER: replace all links with real documentation URLs
const sections = [
  {
    icon: Zap,
    title: 'Getting Started',
    color: 'from-indigo-500 to-violet-500',
    pages: [
      { title: 'Quick Start Guide', desc: 'Be up and running with your first AI agent in under 30 minutes.' },
      { title: 'Architecture Overview', desc: 'How Digian.ai fits into your enterprise data and telephony stack.' },
      { title: 'Authentication & API Keys', desc: 'Generate and manage API credentials for your deployment.' },
      { title: 'Your First Voice Call', desc: 'Place a test call and inspect the full interaction log.' },
    ],
  },
  {
    icon: Headphones,
    title: 'AI Voice Agents',
    color: 'from-violet-500 to-purple-500',
    pages: [
      { title: 'Agent Configuration', desc: 'Define intents, prompts, and escalation logic for your voice agent.' },
      { title: 'ITSM Integrations', desc: 'Connect to ServiceNow, Jira Service Management, and Freshservice.' },
      { title: 'Telephony Setup', desc: 'SIP trunking, Twilio, and direct number configuration.' },
      { title: 'Escalation & Handoff', desc: 'Route calls to human agents with full context transfer.' },
    ],
  },
  {
    icon: Database,
    title: 'Analytics Chat Agents',
    color: 'from-emerald-500 to-cyan-500',
    pages: [
      { title: 'Connecting Data Sources', desc: 'Add Snowflake, Databricks, BigQuery, and other warehouses.' },
      { title: 'Semantic Layer Setup', desc: 'Define business-friendly names, metrics, and data governance rules.' },
      { title: 'Chat Widget Embedding', desc: 'Embed the analytics agent in your internal portals or BI tools.' },
      { title: 'Role-Based Data Access', desc: 'Map your existing RLS and RBAC policies to the AI agent.' },
    ],
  },
  {
    icon: Code2,
    title: 'API Reference',
    color: 'from-blue-500 to-indigo-500',
    pages: [
      { title: 'REST API Overview', desc: 'Base URL, versioning, request format, and error codes.' },
      { title: 'Voice Calls API', desc: 'Initiate, monitor, and retrieve transcripts for voice interactions.' },
      { title: 'Analytics Query API', desc: 'Submit natural language queries and retrieve structured results.' },
      { title: 'Webhooks', desc: 'Receive real-time events for call completion, escalation, and more.' },
    ],
  },
  {
    icon: Shield,
    title: 'Security & Compliance',
    color: 'from-rose-500 to-red-500',
    pages: [
      { title: 'Data Residency Options', desc: 'Configure where your data is processed and stored (UK, EU, US).' },
      { title: 'Security & GDPR Compliance', desc: 'How we handle data security, GDPR alignment, and how to access our security documentation.' },
      { title: 'Audit Logging', desc: 'Enable full audit trails for every agent interaction and query.' },
      { title: 'On-Premise Deployment', desc: 'Deploy Digian.ai agents inside your own VPC or private cloud.' },
    ],
  },
  {
    icon: Book,
    title: 'Guides & Tutorials',
    color: 'from-amber-500 to-orange-500',
    pages: [
      { title: 'IT Self-Service Playbook', desc: 'Design an IT support agent that resolves 70%+ of Tier-1 tickets.' },
      { title: 'Analytics Governance Checklist', desc: 'Ensure your analytics agent respects data policy before go-live.' },
      { title: 'Multi-Language Configuration', desc: 'Enable voice and chat agents in 30+ languages.' },
      { title: 'Migration Accelerator Setup', desc: 'Configure the EDA Toolkit and Data Prep tools for your program.' },
    ],
  },
];

const quickLinks = [
  'Authentication',
  'REST API',
  'Webhooks',
  'ServiceNow Integration',
  'Snowflake Connector',
  'GDPR',
  'Rate Limits',
  'SDKs',
];

const DocsPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEOHead page="docs" />

      {/* Hero */}
      <section className="bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-blue-500/10 py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl mx-auto text-center space-y-6">
            <p className="text-sm font-semibold uppercase tracking-widest text-indigo-600">Documentation</p>
            <h1 className="text-5xl font-bold text-slate-900">Digian.ai Docs</h1>
            <p className="text-lg text-slate-600">
              Everything you need to deploy, configure, and scale Digian.ai's voice agents and analytics tools in your enterprise.
            </p>
            {/* PLACEHOLDER: wire up to real Algolia DocSearch or other search */}
            <div className="relative max-w-xl mx-auto">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                placeholder="Search documentation… (coming soon)"
                disabled
                className="w-full pl-12 pr-4 py-3.5 rounded-xl border border-slate-200 bg-white text-slate-400 text-sm shadow-sm cursor-not-allowed"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Quick links */}
      <section className="bg-white border-b border-slate-100 py-6">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide mr-2">Popular:</span>
            {quickLinks.map(ql => (
              <Link
                key={ql}
                to="/contact"
                className="px-3 py-1 bg-slate-100 hover:bg-indigo-100 hover:text-indigo-700 text-slate-600 rounded-full text-xs font-medium transition-colors"
              >
                {ql}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Doc sections grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {sections.map((section, si) => {
              const Icon = section.icon;
              return (
                <motion.div key={section.title} {...fadeUp} transition={{ delay: si * 0.08 }}
                  className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:border-indigo-300 hover:shadow-sm transition-all">
                  <div className={`bg-gradient-to-r ${section.color} px-6 py-5 flex items-center gap-3`}>
                    <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <h2 className="font-bold text-white">{section.title}</h2>
                  </div>
                  <ul className="divide-y divide-slate-100">
                    {section.pages.map(page => (
                      <li key={page.title}>
                        {/* PLACEHOLDER: replace Link with real docs URL */}
                        <Link to="/contact"
                          className="flex items-center justify-between px-6 py-3.5 hover:bg-slate-50 group transition-colors">
                          <div>
                            <p className="text-sm font-medium text-slate-900 group-hover:text-indigo-700 transition-colors">{page.title}</p>
                            <p className="text-xs text-slate-500 mt-0.5">{page.desc}</p>
                          </div>
                          <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-indigo-500 transition-colors flex-shrink-0 ml-3" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Changelog & API ref strip */}
      <section className="py-16 bg-slate-50 border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                title: 'Changelog',
                desc: 'Track every product update, API change, and new connector release.',
                cta: 'View Changelog',
              },
              {
                title: 'API Reference',
                desc: 'Full OpenAPI spec with live interactive examples for all endpoints.',
                cta: 'Open API Reference',
              },
              {
                title: 'SDKs & Libraries',
                desc: 'Official Python, Node.js, and REST client SDKs with code samples.',
                cta: 'Browse SDKs',
              },
            ].map((item, i) => (
              <motion.div key={item.title} {...fadeUp} transition={{ delay: i * 0.1 }}
                className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="font-semibold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-sm text-slate-600 mb-4">{item.desc}</p>
                {/* PLACEHOLDER: link to real doc resource */}
                <Link to="/contact"
                  className="inline-flex items-center gap-1 text-sm font-medium text-indigo-600 hover:text-indigo-800">
                  {item.cta} <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-to-r from-indigo-600 to-violet-600 py-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <motion.div {...fadeUp} className="space-y-5">
            <h2 className="text-3xl font-bold text-white">Can't find what you need?</h2>
            <p className="text-indigo-200">Our solutions engineering team is happy to help with integrations, custom deployments, and complex configurations.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact"><Button size="lg" variant="secondary">Contact Support</Button></Link>
              <Link to="/demo">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-indigo-600">
                  Book a Technical Demo <ExternalLink className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default DocsPage;
